import React, { useReducer } from 'react';
import dayjs from 'dayjs';
import { cn } from '../../../../../utils/styles';
import ContentCard from '../../../Common/Card';
import Icons from '../../../../../components/Common/Icons/Icons';
import MenuIcon from '../../../Layout/Menu/MenuIcon';
import { Button } from '../../../../../components/Common/UI/Button';
import FilterBlock from './FilterBlock';
import Separator from '../../../../../components/Common/UI/Separator';
import { isObjEqual } from '../../../../../utils';

interface ITableFilters {
  open: boolean;
  handleOpen: (payload: boolean) => void;
  handleApplyFilters: (payload: boolean) => void;
}

type NumberFiltersType = 'between' | 'equals' | 'greater than' | 'less than';

const filters: NumberFiltersType[] = ['between', 'equals', 'greater than', 'less than'];

interface IFilterGroup {
  selected: NumberFiltersType;
  from?: string;
  to?: string;
  amount?: number;
  amount2?: number;
}

export interface IFiltersState {
  group1: IFilterGroup;
  group2: IFilterGroup;
  group3: IFilterGroup;
}

const currentDay = dayjs().day();
const currentMonth = dayjs().month();
const currentYear = dayjs().year();

const defaultValue: IFiltersState = {
  group1: {
    selected: filters[0],
    from: dayjs().subtract(2, 'month').startOf('month').subtract(1, 'year').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  },
  group2: { selected: filters[0], amount: 0, amount2: 0 },
  group3: { selected: filters[0], amount: 0, amount2: 0 },
};

function TableFilters({ open, handleOpen, handleApplyFilters }: ITableFilters) {
  const [state, dispatch] = useReducer(
    (prevState: IFiltersState, action: Partial<IFiltersState>) => ({ ...prevState, ...action }),
    defaultValue,
  );

  const handleDispatch = (payload: Partial<IFiltersState>) => {
    dispatch(payload);
  };

  return (
    <ContentCard
      title="Filters"
      className={cn(
        'sm:w-[450px] w-[85%] transition-all lg:h-[660px] h-100vh bg-white p-[20px] right-0 ' +
          'translate-x-[650px] lg:absolute fixed top-0 bottom-0 shadow-lg lg:rounded-[10px] rounded-none',
        {
          'translate-x-[0] overflow-y-scroll': open,
        },
      )}
      icon={
        <MenuIcon icon={<Icons.Filters />} className="bg-addition9 border-addition9" variant="white" size="large" />
      }
      actions={
        <Button onClick={() => handleOpen(false)} variant="ghost" className="p-[5px] h-auto cursor-pointer">
          <Icons.PlusFilled className="rotate-45" />
        </Button>
      }
    >
      <div className="grid gap-[20px]">
        <FilterBlock
          filters={filters}
          title="Check Date"
          group="group1"
          state={state}
          handleDispatch={handleDispatch}
        />
        <Separator className="bg-addition6-20" />
        <FilterBlock
          filters={filters}
          title="Check Number"
          group="group2"
          state={state}
          handleDispatch={handleDispatch}
        />
        <Separator className="bg-addition6-20" />
        <FilterBlock
          filters={filters}
          title="Check Amount"
          group="group3"
          state={state}
          handleDispatch={handleDispatch}
        />
        <div className="mt-[10px]">
          <Separator className="bg-addition6-20 mb-[20px]" />
          <div className="flex justify-end gap-[10px]">
            <Button
              variant="link"
              className="text-primary underline"
              onClick={() => {
                handleDispatch(defaultValue);
                handleApplyFilters(false);
              }}
            >
              Clear
            </Button>

            <Button
              className="!bg-primary hover:opacity-80"
              onClick={() => handleApplyFilters(!isObjEqual(state, defaultValue))}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </ContentCard>
  );
}

export default TableFilters;
